import { create } from 'zustand';
import { authService } from '../services/auth.service';
import { readSession, subscribeSession } from '../services/session-storage';

export type UserRole = 'User' | 'Seller' | 'Moderator' | 'Admin';

export interface UserInfo {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

interface AuthState {
  user: UserInfo | null;
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: UserInfo | null) => void;
  setTokens: (token: string, refreshToken: string) => void;
  checkAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  token: null,
  refreshToken: null,
  isLoading: true,

  login: async (email: string, password: string) => {
    const response = await authService.login(email, password);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Ошибка авторизации');
    }

    const { token, refreshToken, userInfo } = response.data;

    authService.saveTokens(token, refreshToken, userInfo);

    set({
      user: userInfo,
      isAuthenticated: true,
      token,
      refreshToken,
    });
  },

  logout: () => {
    authService.clearTokens();
    set({
      user: null,
      isAuthenticated: false,
      token: null,
      refreshToken: null,
    });
  },

  setUser: (user) => {
    set(user ? { user, isAuthenticated: true } : {
      user: null, isAuthenticated: false, token: null, refreshToken: null,
    });
  },

  setTokens: (token, refreshToken) => {
    set({ token, refreshToken });
  },

  checkAuth: () => {
    const session = readSession();
    if (session) {
      set({
        user: session.userInfo,
        isAuthenticated: true,
        token: session.token,
        refreshToken: session.refreshToken,
        isLoading: false,
      });
    } else {
      set({
        user: null,
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        isLoading: false,
      });
    }
  },
}));

// Обновление токенов, выход и вход в другой вкладке синхронизируют состояние UI.
subscribeSession(() => useAuthStore.getState().checkAuth());
